import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { inr, placeOrder, createRazorpayOrder, verifyRazorpayPayment, cancelRazorpayOrder, formatApiError } from "../lib/api";
import { metaTrack, cartEventData } from "../analytics/metaPixel";
import { Input } from "../components/ui/input";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";

const FIELDS = [
  { name: "name", label: "Full Name", placeholder: "Your full name", full: false },
  { name: "phone", label: "Phone (WhatsApp)", placeholder: "10-digit mobile number", full: false },
  { name: "email", label: "Email (optional)", placeholder: "you@example.com", full: true },
  { name: "address", label: "Address", placeholder: "House no, street, area", full: true },
  { name: "city", label: "City", placeholder: "City", full: false },
  { name: "state", label: "State", placeholder: "State", full: false },
  { name: "pincode", label: "Pincode", placeholder: "6-digit pincode", full: false },
];

export default function CheckoutPage() {
  const { cart, cartTotal, clearCart } = useShop();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "" });
  const [payment, setPayment] = useState("online");
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);

  const shipping = cartTotal >= 2000 || cartTotal === 0 ? 0 : 150;
  const codCharge = payment === "cod" ? 150 : 0;
  const total = cartTotal + shipping + codCharge;

  useEffect(() => {
    if (cart.length) metaTrack("InitiateCheckout", cartEventData(cart, cartTotal));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firePurchase = (res) => {
    if (!sessionStorage.getItem(`meta-purchase-${res.id}`)) {
      metaTrack("Purchase", {
        content_ids: res.items.map((i) => String(i.product_id)),
        contents: res.items.map((i) => ({ id: String(i.product_id), quantity: i.qty })),
        content_type: "product",
        num_items: res.items.reduce((n, i) => n + i.qty, 0),
        value: Number(res.total),
        currency: "INR",
        order_id: res.id,
      }, `purchase-${res.id}`);
      sessionStorage.setItem(`meta-purchase-${res.id}`, "1");
    }
    if (!localStorage.getItem("rvi_first_order_done")) {
      metaTrack("CompleteRegistration", { content_name: "First order customer", status: "ordered" });
      localStorage.setItem("rvi_first_order_done", "1");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openRazorpay = (rzp, orderId) =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        const instance = new window.Razorpay({
          key: rzp.key_id,
          amount: rzp.amount,
          currency: "INR",
          name: "R V Ilkal Sarees",
          description: `Order ${orderId}`,
          order_id: rzp.razorpay_order_id,
          prefill: { name: form.name, contact: form.phone, ...(form.email ? { email: form.email } : {}) },
          theme: { color: "#6B1C23" },
          handler: (resp) => resolve(resp),
          modal: { ondismiss: () => resolve(null) },
        });
        instance.open();
      };
      script.onerror = () => resolve(null);
      document.body.appendChild(script);
    });

  const submit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return toast.error("Your bag is empty");
    if (!form.name.trim() || form.phone.trim().length < 10 || !form.address.trim() || !form.city.trim() || !form.state.trim() || form.pincode.trim().length < 6) {
      return toast.error("Please fill all required fields correctly");
    }
    setPlacing(true);
    try {
      const res = await placeOrder({
        customer: { ...form, email: form.email || undefined },
        items: cart.map((i) => ({ product_id: i.id, name: i.name, price: i.price, qty: i.qty })),
        payment_method: payment,
      });
      if (payment === "cod") {
        firePurchase(res);
        setOrder(res);
        clearCart();
        return;
      }
      const rzp = await createRazorpayOrder(res.id);
      const resp = await openRazorpay(rzp, res.id);
      if (!resp) {
        await cancelRazorpayOrder(res.id).catch(() => {});
        toast.error(`Payment not completed — order ${res.id} was cancelled. Your bag is unchanged, try again when ready.`);
        return;
      }
      await verifyRazorpayPayment({ order_id: res.id, ...resp });
      firePurchase(res);
      setOrder(res);
      clearCart();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPlacing(false);
    }
  };

  if (order) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="order-success">
        <CheckCircle2 className="w-16 h-16 text-maroon mx-auto" strokeWidth={1.25} />
        <h1 className="font-heading text-3xl sm:text-4xl text-charcoal mt-6">
          {order.payment_method === "cod" ? "Order Placed. Dhanyavaada!" : "Payment Successful. Dhanyavaada!"}
        </h1>
        <p className="text-charcoal/60 mt-4 leading-relaxed">
          Your order <span className="font-semibold text-maroon" data-testid="order-id">{order.id}</span> for{" "}
          <span className="font-semibold">{inr(order.total)}</span>{" "}
          {order.payment_method === "cod"
            ? "is placed. It will be confirmed once our team verifies it — we will call you on your phone number."
            : "is confirmed — your online payment was received successfully. Our team will dispatch your saree soon."}
        </p>
        <div className="flex justify-center mt-10">
          <Link to="/shop" data-testid="order-continue-btn" className="border border-gold text-charcoal text-xs tracking-[0.18em] uppercase px-8 py-4 hover:bg-gold/10">
            Continue Shopping
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16" data-testid="checkout-page">
      <p className="uppercase tracking-[0.25em] text-xs font-semibold text-gold">Almost yours</p>
      <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-charcoal mt-2">Checkout</h1>

      {cart.length === 0 ? (
        <div className="text-center py-24" data-testid="checkout-empty">
          <p className="text-charcoal/60">Your bag is empty.</p>
          <Link to="/shop" data-testid="checkout-shop-link" className="inline-block mt-6 bg-maroon text-ivory text-xs tracking-[0.18em] uppercase px-8 py-3.5 hover:bg-maroon-dark">
            Shop Sarees
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="grid lg:grid-cols-[1fr_400px] gap-10 lg:gap-16 mt-10">
          <div>
            <h2 className="font-heading text-xl text-charcoal">Delivery Details</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                  <label className="text-xs tracking-[0.12em] uppercase text-charcoal/50">{f.label}</label>
                  <Input
                    value={form[f.name]}
                    onChange={set(f.name)}
                    placeholder={f.placeholder}
                    data-testid={`checkout-input-${f.name}`}
                    className="mt-2 bg-ivory border-gold/40 rounded-sm h-12 focus-visible:ring-gold"
                  />
                </div>
              ))}
            </div>

            <h2 className="font-heading text-xl text-charcoal mt-10">Payment</h2>
            <RadioGroup value={payment} onValueChange={(v) => { setPayment(v); metaTrack("AddPaymentInfo", { ...cartEventData(cart, cartTotal + shipping + (v === "cod" ? 150 : 0)), payment_type: v }); }} className="mt-6 space-y-3" data-testid="payment-methods">
              <label className={`flex items-start gap-3 border p-5 cursor-pointer transition-colors ${payment === "online" ? "border-maroon bg-surface/50" : "border-gold/30"}`}>
                <RadioGroupItem value="online" id="online" data-testid="payment-online" className="mt-0.5 border-gold/60 text-maroon" />
                <span>
                  <span className="block text-sm font-semibold text-charcoal">Pay Online — UPI, Cards, NetBanking</span>
                  <span className="block text-xs text-charcoal/50 mt-1">Secure payment via Razorpay. No extra charges.</span>
                </span>
              </label>
              <label className={`flex items-start gap-3 border p-5 cursor-pointer transition-colors ${payment === "cod" ? "border-maroon bg-surface/50" : "border-gold/30"}`}>
                <RadioGroupItem value="cod" id="cod" data-testid="payment-cod" className="mt-0.5 border-gold/60 text-maroon" />
                <span>
                  <span className="block text-sm font-semibold text-charcoal">Cash on Delivery</span>
                  <span className="block text-xs text-charcoal/50 mt-1">Pay when your saree arrives at your door. A ₹150 COD handling fee applies.</span>
                </span>
              </label>
            </RadioGroup>
          </div>

          <aside className="bg-surface/60 border border-gold/25 p-6 sm:p-8 h-fit" data-testid="order-summary">
            <h2 className="font-heading text-xl text-charcoal">Order Summary</h2>
            <div className="mt-6 space-y-4">
              {cart.map((i) => (
                <div key={i.id} className="flex gap-3 items-center">
                  <img src={i.image} alt={i.name} className="w-14 h-[4.5rem] object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-charcoal truncate">{i.name}</p>
                    <p className="text-xs text-charcoal/50">Qty {i.qty}</p>
                  </div>
                  <p className="text-sm text-charcoal">{inr(i.price * i.qty)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-gold/25 mt-6 pt-5 space-y-2 text-sm">
              <div className="flex justify-between text-charcoal/60"><span>Subtotal</span><span data-testid="summary-subtotal">{inr(cartTotal)}</span></div>
              <div className="flex justify-between text-charcoal/60"><span>Shipping</span><span data-testid="summary-shipping">{shipping === 0 ? "Free" : inr(shipping)}</span></div>
              {codCharge > 0 && (
                <div className="flex justify-between text-charcoal/60"><span>COD Fee</span><span data-testid="summary-cod-fee">{inr(codCharge)}</span></div>
              )}
              <div className="flex justify-between text-charcoal font-semibold text-base pt-2 border-t border-gold/25"><span>Total</span><span data-testid="summary-total">{inr(total)}</span></div>
            </div>
            <button
              type="submit"
              disabled={placing}
              data-testid="place-order-btn"
              className="w-full mt-6 bg-maroon text-ivory text-xs tracking-[0.18em] uppercase py-4 hover:bg-maroon-dark transition-colors disabled:opacity-60"
            >
              {placing ? "Processing…" : payment === "online" ? `Pay ${inr(total)} Securely` : "Place Order"}
            </button>
            <p className="text-[11px] text-charcoal/50 mt-4 text-center">Every saree is premium quality checked before dispatch.</p>
          </aside>
        </form>
      )}
    </main>
  );
}
